import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { config } from '../../config';
import './scss/AICardSuggestions.scss';
import MemberImage from '../Global/MemberImage';
import Refresh from '../icons/Refresh';
import Close from '../icons/Close';
import TaskAlt from '../icons/TaskAlt';
import ListAlt from '../icons/ListAlt';
import Schedule from '../icons/Schedule';
import Comment from '../icons/Comment';
import AttachFile from '../icons/AttachFile';
import Checklist from '../icons/Checklist';
import Lightbulb from '../icons/Lightbulb';
import Psychology from '../icons/Psychology';

const AICardSuggestions = ({
  boardId,
  onClose,
  limit = 5
}) => {
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalCards, setTotalCards] = useState(0);

  useEffect(() => {
    if (boardId) {
      fetchRecommendations();
    }
  }, [boardId]);

  const fetchRecommendations = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(
        `${config.API_URI}/v1/ai/boards/${boardId}/card-recommendations?limit=${limit}&includeAssigned=true`,
        {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch recommendations');
      }

      const data = await response.json();
      if (data.success) {
        setRecommendations(data.recommendations || []);
        setTotalCards(data.totalCards || 0);
      } else {
        throw new Error(data.error || 'Failed to get recommendations');
      }
    } catch (err) {
      console.error('Card recommendations error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchRecommendations();
  };

  const handleClose = () => {
    if (onClose) {
      onClose();
    }
  };

  const getPriorityColor = (card) => {
    const priority = card.priority?.toLowerCase() || 'normal';
    switch (priority) {
      case 'critical':
      case 'urgent':
        return '#ff4444';
      case 'high':
        return '#ff8800';
      case 'medium':
      case 'normal':
        return '#4CAF50';
      case 'low':
        return '#9E9E9E';
      default:
        return '#4CAF50';
    }
  };

  const formatDueDate = (dueDate) => {
    if (!dueDate?.date) return null;

    const now = new Date();
    const due = new Date(dueDate.date);
    const diffDays = Math.ceil((due - now) / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return { text: 'Overdue', color: '#ff4444' };
    if (diffDays === 0) return { text: 'Due today', color: '#ff8800' };
    if (diffDays === 1) return { text: 'Due tomorrow', color: '#ff8800' };
    if (diffDays <= 7) return { text: `Due in ${diffDays} days`, color: '#4CAF50' };

    return { text: due.toLocaleDateString(), color: '#9E9E9E' };
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  if (loading) {
    return (
      <div className="ai-suggestions-overlay" onClick={handleOverlayClick}>
        <div className="ai-suggestions-modal">
          <div className="ai-loading">
            <div className="loading-spinner"></div>
            <p>Analyzing your cards...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ai-suggestions-overlay" onClick={handleOverlayClick}>
      <div className="ai-suggestions-modal">
        {/* Header */}
        <header className="ai-header">
          <div className="header-content">
            <h2>Card Recommendations</h2>
            <p>Cards worth working on next</p>
          </div>
          <div className="header-actions">
            <button
              onClick={handleRefresh}
              className="btn-icon"
              disabled={loading}
              title="Refresh recommendations"
            >
              <Refresh size={24} />
            </button>
            <button
              onClick={handleClose}
              className="btn-icon btn-close"
              title="Close"
            >
              <Close size={24} />
            </button>
          </div>
        </header>

        {/* Content */}
        <main className="ai-content">
          {error && (
            <div className="ai-error">
              <p>Something went wrong</p>
              <button onClick={handleRefresh} className="btn-retry">
                Try Again
              </button>
            </div>
          )}

          {!error && recommendations.length === 0 && !loading && (
            <div className="ai-empty">
              <TaskAlt size={48} />
              <p>No actionable cards found</p>
              <small>All cards are either completed or blocked</small>
            </div>
          )}

          {!error && recommendations.length > 0 && (
            <>
              <div className="recommendations-stats">
                <span>{recommendations.length} of {totalCards} cards recommended</span>
              </div>

              <div className="suggestions-list">
                {recommendations.map((card, index) => {
                  const dueInfo = formatDueDate(card.dueDate);

                  return (
                    <div key={card._id} className="recommendation-card">
                      <div className="card-rank">#{index + 1}</div>

                      <div className="card-content">
                        <div className="card-header">
                          <Link
                            to={`/c/${card.shortId}/${card.slug}`}
                            className="card-title"
                            onClick={handleClose}
                          >
                            {card.title}
                          </Link>
                          <div className="card-score">
                            <span className="score-value">{card.score}</span>
                            <span className="score-label">score</span>
                          </div>
                        </div>

                        <div className="card-meta">
                          <div className="card-list">
                            <ListAlt size={18} />
                            <span>{card.actionList?.title || 'Unknown List'}</span>
                          </div>

                          {card.priority && (
                            <div className="card-priority">
                              <span
                                className="priority-indicator"
                                style={{ backgroundColor: getPriorityColor(card) }}
                              ></span>
                              <span>{card.priority}</span>
                            </div>
                          )}

                          {dueInfo && (
                            <div className="card-due" style={{ color: dueInfo.color }}>
                              <Schedule size={18} />
                              <span>{dueInfo.text}</span>
                            </div>
                          )}
                        </div>

                        <div className="card-details">
                          {card.users && card.users.length > 0 && (
                            <div className="card-members">
                              <MemberImage members={card.users} size={24} displayMembersLimit={3} />
                            </div>
                          )}

                          <div className="card-stats">
                            {(card.commentCount > 0 || (card.comments && card.comments.length > 0)) && (
                              <span className="stat">
                                <Comment size={16} />
                                {card.commentCount || card.comments?.length || 0}
                              </span>
                            )}
                            {card.attachments && card.attachments.length > 0 && (
                              <span className="stat">
                                <AttachFile size={16} />
                                {card.attachments.length}
                              </span>
                            )}
                            {card.checklists && card.checklists.length > 0 && (
                              <span className="stat">
                                <Checklist size={16} />
                                {card.checklists.reduce((total, cl) => total + (cl.items?.length || 0), 0)}
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="recommendation-reason">
                          <Lightbulb size={18} />
                          <span>{card.aiInsight || card.recommendation}</span>
                        </div>

                        {card.aiInsight && (
                          <div className="ai-insight">
                            <Psychology size={18} />
                            <span>AI: {card.aiInsight}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </main>


      </div>
    </div>
  );
};

export default AICardSuggestions;